import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Tooltip from './components/Tooltip';
import { getRodmanStats, fetchGames } from './api';
import rodmanLogo from './rodman_logo.jpg';
import './Home.css';

// League logo URLs - fallback logos if API doesn't provide them
const LEAGUE_LOGOS = {
    'NBA': 'https://cdn.nba.com/manage/league/nba-logo.svg',
    'PremierLeague': 'https://upload.wikimedia.org/wikipedia/en/f/f2/Premier_League_Logo.png'
};

// Function to get league logo URL - same logic as GameList.js
const getLeagueLogo = (league, leagueBadge) => {
    // First try to use the league badge from API
    if (leagueBadge && leagueBadge.trim() !== '') {
        return leagueBadge;
    }
    
    // Fallback to our predefined logos
    return LEAGUE_LOGOS[league] || '';
};

const Home = () => {
    const navigate = useNavigate();
    const [stats, setStats] = useState(null);
    const [accuracy, setAccuracy] = useState(''); // Default fallback
    const [leagueBadges, setLeagueBadges] = useState({
        NBA: null,
        PremierLeague: null
    });

    useEffect(() => {
        const loadData = async () => {
            try {
                // Load stats and games in parallel
                const [statsData, gamesData] = await Promise.allSettled([
                    getRodmanStats(),
                    fetchGames()
                ]);
                
                // Handle stats
                if (statsData.status === 'fulfilled') {
                    setStats(statsData.value);
                    
                    // Extract accuracy from stats like in Rodman.js
                    if (statsData.value?.accuracy) {
                        const accuracyMatch = statsData.value.accuracy.match(/(\d+(?:\.\d+)?)%/);
                        if (accuracyMatch) {
                            setAccuracy(`${accuracyMatch[1]}%`);
                        }
                    }
                }
                
                // Handle games to extract league badges
                if (gamesData.status === 'fulfilled' && Array.isArray(gamesData.value)) {
                    const games = gamesData.value;
                    const badges = {};
                    
                    // Find a game from each league to get their badges
                    const nbaGame = games.find(game => game.league === 'NBA');
                    const plGame = games.find(game => game.league === 'PremierLeague');
                    
                    if (nbaGame?.league_badge) {
                        badges.NBA = nbaGame.league_badge;
                    }
                    if (plGame?.league_badge) {
                        badges.PremierLeague = plGame.league_badge;
                    }
                    
                    setLeagueBadges(badges);
                }
            } catch (error) {
                console.error('Error loading data for about page:', error);
                // Keep defaults on error
            }
        };
        
        loadData();
    }, []);

    return (
        <div className="about-container">
            {/* Hero Section */}
            <div className="hero-section">
                <img src="/logo_v2.png" alt="Open Score Logo" className="main-logo" />
                <h1 className="hero-title">Open Score</h1>
                <p className="hero-subtitle">AI-Powered Sports Analytics Platform</p>
                
                <div className="hero-stats">
                    <div className="stat-item">
                        <span className="stat-value">{accuracy}</span>
                        <span className="stat-label">Prediction Accuracy</span>
                    </div>
                    <div className="stat-item">
                        <span className="stat-value">2</span>
                        <span className="stat-label">Sports Leagues</span>
                    </div>
                    <div className="stat-item">
                        <span className="stat-value">Real-time</span>
                        <span className="stat-label">Analytics</span>
                    </div>
                </div>
            </div>

            {/* RODMAN AI Spotlight */}
            <div className="rodman-spotlight">
                <div className="rodman-header">
                    <img src={rodmanLogo} alt="RODMAN AI" className="rodman-avatar" />
                    <div className="rodman-info">
                        <h2>Meet RODMAN AI</h2>
                        <p>Your bold, analyst-like sports prediction engine with {accuracy} overall accuracy and even higher precision on confident picks.</p>
                    </div>
                </div>
                
                <Tooltip 
                    content="Experience RODMAN AI's game analysis and predictions"
                    position="top"
                    delay={300}
                >
                    <button 
                        className="ask-rodman-btn hero-rodman"
                        onClick={() => navigate('/rodman')}
                    >
                        ⚡ Chat with RODMAN
                    </button>
                </Tooltip>
            </div>

            {/* Core Features */}
            <div className="features-section">
                <h2 className="section-title">Core Features</h2>
                
                <div className="features-grid">
                    <div className="feature-card">
                        <div className="feature-icon">
                            {(() => {
                                const nbaLogo = getLeagueLogo('NBA', leagueBadges.NBA);
                                return nbaLogo ? (
                                    <img 
                                        src={nbaLogo}
                                        alt="NBA" 
                                        className="league-icon"
                                        onError={(e) => {
                                            e.target.style.display = 'none';
                                            e.target.parentNode.innerHTML = '🏀';
                                        }}
                                    />
                                ) : '🏀';
                            })()}
                        </div>
                        <h3>NBA Analytics</h3>
                        <p>Real-time game statistics, box scores, and power rankings with advanced metrics including hustle stats and clutch performance.</p>
                    </div>
                    
                    <div className="feature-card">
                        <div className="feature-icon">
                            {(() => {
                                const plLogo = getLeagueLogo('PremierLeague', leagueBadges.PremierLeague);
                                return plLogo ? (
                                    <img 
                                        src={plLogo}
                                        alt="Premier League" 
                                        className="league-icon"
                                        onError={(e) => {
                                            e.target.style.display = 'none';
                                            e.target.parentNode.innerHTML = '⚽';
                                        }}
                                    />
                                ) : '⚽';
                            })()}
                        </div>
                        <h3>Premier League</h3>
                        <p>Comprehensive football analytics with Poisson-based predictions, form analysis, and home advantage calculations.</p>
                    </div>
                    
                    <div className="feature-card">
                        <div className="feature-icon">🧠</div>
                        <h3>AI Predictions</h3>
                        <p>Machine learning-powered predictions using Random Forest models with 46 optimized features and probability calibration.</p>
                    </div>
                    
                    <div className="feature-card">
                        <div className="feature-icon">📊</div>
                        <h3>Power Rankings</h3>
                        <p>Real-time Elo ratings and team performance tiers with playoff probabilities and championship odds.</p>
                    </div>
                    
                    <div className="feature-card">
                        <div className="feature-icon">⚡</div>
                        <h3>Interactive AI</h3>
                        <p>Ask RODMAN buttons throughout the interface for instant game analysis and personalized insights.</p>
                    </div>
                    
                    <div className="feature-card">
                        <div className="feature-icon">🏗️</div>
                        <h3>Pro Infrastructure</h3>
                        <p>Heroku deployment with Redis caching, Docker containers, and 99.5% uptime reliability.</p>
                    </div>
                </div>
            </div>

            {/* Technology Stack */}
            <div className="tech-section">
                <h2 className="section-title">Tech Stack</h2>
                <div className="tech-grid">
                    <div className="tech-item">
                        <span className="tech-label">Frontend</span>
                        <span className="tech-value">React + Node.js</span>
                    </div>
                    <div className="tech-item">
                        <span className="tech-label">Backend</span>
                        <span className="tech-value">Go + Python FastAPI</span>
                    </div>
                    <div className="tech-item">
                        <span className="tech-label">ML Engine</span>
                        <span className="tech-value">Random Forest + Scikit-learn</span>
                    </div>
                    <div className="tech-item">
                        <span className="tech-label">Database</span>
                        <span className="tech-value">MySQL + Redis</span>
                    </div>
                    <div className="tech-item">
                        <span className="tech-label">Deployment</span>
                        <span className="tech-value">Docker + Heroku</span>
                    </div>
                    <div className="tech-item">
                        <span className="tech-label">Performance</span>
                        <span className="tech-value">&lt;200ms response time</span>
                    </div>
                </div>
            </div>

            {/* Upcoming Features */}
            <div className="roadmap-section">
                <h2 className="section-title">Coming Soon</h2>
                <div className="roadmap-grid">
                    <div className="roadmap-item">
                        <h3>🎯 RODMAN's Weekly Picks</h3>
                        <p>3-5 curated predictions with bold, confident analysis and post-game recaps.</p>
                        <span className="timeline">2026</span>
                    </div>
                    
                    <div className="roadmap-item">
                        <h3>📱 Mobile App</h3>
                        <p>Native iOS/Android applications with live tracking and push notifications.</p>
                        <span className="timeline">2026</span>
                    </div>
                    
                    <div className="roadmap-item">
                        <h3>🌍 Multi-League</h3>
                        <p>International basketball leagues and expanded football coverage.</p>
                        <span className="timeline">2026</span>
                    </div>
                </div>
            </div>

            {/* Call to Action */}
            <div className="cta-section">
                <h2>Ready to Explore?</h2>
                <p>Dive into professional-grade sports analytics powered by AI</p>
                <div className="cta-buttons">
                    <button 
                        className="cta-button primary"
                        onClick={() => navigate('/')}
                    >
                        View Games
                    </button>
                    <button 
                        className="cta-button secondary"
                        onClick={() => navigate('/standings')}
                    >
                        Power Rankings
                    </button>
                    <button 
                        className="cta-button accent"
                        onClick={() => navigate('/rodman')}
                    >
                        ⚡ Try RODMAN AI
                    </button>
                </div>
            </div>
        </div>
    );
};

export default Home;
